/*
    Flow logic
    For each decision box it is a yes or no logic that determines which box is next. 
    If a yes button is clicked and then the no button is clicked the flow and visible states 
    need to change appropriatly. Once a button is clicked focus should move to the next box that is active.

    BOX 1
    IF db1 Yes button is clicked 
    * set aria-pressed=true
    * db2 box remove inert and set aria-hidden=false add class of box-visible
    IF db1 No button is clicked
    * set aria-pressed=true
    * ne1 box remove inert and set aria-hidden=false add class of box-visible 

    Box 2
    IF db2 Yes button is clicked
    * set aria-pressed=true
    * db3 box remove inert and set aria-hidden=false add class of box-visible
    IF db2 No button is clicked
    * set aria-pressed=true
    * ne2 box remove inert and set aria-hidden=false add class of box-visible

    Box 3
    IF db3 Yes button is clicked
    * set aria-pressed=true
    * ne3 box remove inert and set aria-hidden=false add class of box-visible
    IF db3 No button is clicked
    * set aria-pressed=true
    * db4 box remove inert and set aria-hidden=false add class of box-visible
    * db5 box remove inert and set aria-hidden=false add class of box-visible

    Box 4
    IF db4 Yes button is clicked
    * set aria-pressed=true
    * db6 box remove inert and set aria-hidden=false add class of box-visible
    * If db5 yes button was already clicked then reverse that flow (db5 should still remain visible)

    Box 5
    IF db5 Yes button is clicked
    * set aria-pressed=true
    * db7 box remove inert and set aria-hidden=false add class of box-visible
    * If db4 already had the yes pressed reverse that flow (db4 should still remain visible)
    
    Box 6
    IF db6 Yes button is clicked
    * set aria-pressed=true
    * db8 box remove inert and set aria-hidden=false add class of box-visible
    IF db6 No button is clicked
    * set aria-pressed=true
    * db7 box remove inert and set aria-hidden=false add class of box-visible

    Box 7
    If db7 Yes button is clicked
    * set aria-pressed=true
    * db8 box remove inert and set aria-hidden=false add class of box-visible

    Box 8
    If db8 Yes button is clicked
    * set aria-pressed=true
    * final box remove inert and set aria-hidden=false add class of box-visible
    IF db8 No button is clicked
    * set aria-pressed=true
    * ne4 box remove inert and set aria-hidden=false add class of box-visible
*/

(() => {
    const workflow = document.querySelector('.eligibility-workflow');

    // Bail out safely if this script is loaded on a page without the workflow.
    if (!workflow) {
        return;
    }

    // Map each decision box to the next box(es) for Yes / No.
    // The key names must match the IDs in the Twig template.
    const flowMap = {
        db1: { yes: ['db2'], no: ['ne1'] },
        db2: { yes: ['db3'], no: ['ne2'] },
        db3: { yes: ['ne3'], no: ['db4', 'db5'] },
        db4: { yes: ['db6'] },
        db5: { yes: ['db7'] },
        db6: { yes: ['db8'], no: ['db7'] },
        db7: { yes: ['db8'] },
        db8: { yes: ['final'], no: ['ne4'] },
    };

    const allFlowIds = [
        'db1', 'db2', 'db3', 'db4', 'db5', 'db6', 'db7', 'db8',
        'ne1', 'ne2', 'ne3', 'ne4', 'final'
    ];

    const nodes = allFlowIds.reduce((acc, id) => {
        acc[id] = document.getElementById(id);
        return acc;
    }, {});

    const srStatus = document.getElementById('eligibility-status');

    // Utility to announce updates for assistive technologies.
    function announce(message) {
        if (!srStatus) {
            return;
        }

        srStatus.textContent = '';
        window.setTimeout(() => {
            srStatus.textContent = message;
        }, 20);
    }

    // Build an adjacency list from decision boxes so we can recursively reset
    // all downstream boxes whenever an upstream answer changes.
    const adjacency = Object.entries(flowMap).reduce((acc, [sourceId, outcomes]) => {
        const yesTargets = outcomes.yes || [];
        const noTargets = outcomes.no || [];
        acc[sourceId] = [...yesTargets, ...noTargets];
        return acc;
    }, {});

    function getDescendants(sourceId) {
        const visited = new Set();
        const queue = [...(adjacency[sourceId] || [])];

        while (queue.length) {
            const current = queue.shift();
            if (visited.has(current)) {
                continue;
            }

            visited.add(current);
            (adjacency[current] || []).forEach((next) => queue.push(next));
        }

        return visited;
    }

    function isDecisionBox(element) {
        return Boolean(element && element.matches('.decision-box'));
    }

    function setVisible(element) {
        if (!element) {
            return;
        }

        element.classList.add('box-visible');
        element.removeAttribute('inert');
        element.setAttribute('aria-hidden', 'false');
    }

    function setHidden(element) {
        if (!element) {
            return;
        }

        element.classList.remove('box-visible');
        element.setAttribute('inert', '');
        element.setAttribute('aria-hidden', 'true');

        // Clear button state when a branch is hidden so re-answering is predictable.
        if (isDecisionBox(element)) {
            const buttons = element.querySelectorAll('button[aria-pressed]');
            buttons.forEach((btn) => btn.setAttribute('aria-pressed', 'false'));
        }
    }

    function focusFirstInteractive(element) {
        if (!element) {
            return;
        }

        // Focus the revealed box container so users get the new context first.
        element.setAttribute('tabindex', '-1');
        element.focus();
    }

    function getLegendText(box) {
        const legend = box.querySelector('legend');
        return legend ? legend.textContent.trim() : 'Question';
    }

    function resetDownstreamFrom(sourceId) {
        const descendants = getDescendants(sourceId);
        descendants.forEach((id) => {
            const node = nodes[id];
            if (node) {
                setHidden(node);
            }
        });
    }

    // Box 4 and Box 5 together represent a single branching choice.
    // Since each UI card only has a "Yes" button, we treat clicking one as
    // the opposite of clicking the other and clear the alternate path.
    function getMutuallyExclusiveSources(boxId, answer) {
        if (answer !== 'yes') {
            return [boxId];
        }

        if (boxId === 'db4') {
            return ['db4', 'db5'];
        }

        if (boxId === 'db5') {
            return ['db5', 'db4'];
        }

        return [boxId];
    }

    function resetSiblingChoiceState(boxId, answer) {
        if (answer !== 'yes') {
            return;
        }

        if (boxId === 'db4') {
            const siblingButton = nodes.db5 && nodes.db5.querySelector('button[data-answer="yes"]');
            if (siblingButton) {
                siblingButton.setAttribute('aria-pressed', 'false');
            }
        }

        if (boxId === 'db5') {
            const siblingButton = nodes.db4 && nodes.db4.querySelector('button[data-answer="yes"]');
            if (siblingButton) {
                siblingButton.setAttribute('aria-pressed', 'false');
            }
        }
    }

    function applyAnswer(box, answer) {
        const boxId = box.id;
        const rule = flowMap[boxId];

        if (!rule || !rule[answer]) {
            return;
        }

        const clickedButton = box.querySelector(`button[data-answer="${answer}"]`);
        const otherAnswer = answer === 'yes' ? 'no' : 'yes';
        const otherButton = box.querySelector(`button[data-answer="${otherAnswer}"]`);

        // Keep pressed states accurate for assistive technology.
        if (clickedButton) {
            clickedButton.setAttribute('aria-pressed', 'true');
        }
        if (otherButton) {
            otherButton.setAttribute('aria-pressed', 'false');
        }

        // Box 4 / Box 5 are treated as one shared choice. We reset both
        // downstream paths before showing the selected target path.
        const resetSources = getMutuallyExclusiveSources(boxId, answer);
        resetSources.forEach((sourceId) => resetDownstreamFrom(sourceId));

        // Ensure the sibling choice in the paired Box 4/5 UI is not left pressed.
        resetSiblingChoiceState(boxId, answer);

        const targets = rule[answer]
            .map((id) => nodes[id])
            .filter(Boolean);

        targets.forEach((target) => setVisible(target));

        // Move focus to the first revealed item so keyboard/screen-reader users
        // are taken directly to the newly relevant content.
        focusFirstInteractive(targets[0]);

        const answerLabel = answer === 'yes' ? 'Yes' : 'No';
        announce(`${getLegendText(box)} Answer selected: ${answerLabel}.`);
    }

    // Single delegated listener keeps behavior maintainable and resilient
    // even if button markup is adjusted in the template later.
    workflow.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-answer]');
        if (!button || !workflow.contains(button)) {
            return;
        }

        const box = button.closest('.decision-box');
        const answer = button.dataset.answer;

        if (!box || !answer) {
            return;
        }

        applyAnswer(box, answer);
    });
})();
